import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, ArrowRight, Loader2 } from 'lucide-react';
import { authAPI } from '../../services/api';
import JourneyLayout from '../../components/JourneyLayout';
import { eventToRouteMap } from '../../Utils/constants';

const residenceOptions = [
  { label: 'Owned', value: 'owned', id: 1 },
  { label: 'Rented', value: 'rented', id: 2 },
  { label: 'Company Provided', value: 'company_provided', id: 3 },
  { label: 'Parental Home', value: 'parental_home', id: 4 },
];

const Section = ({ title, children }) => (
  <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-6 space-y-5 shadow-md">
    <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
    {children}
  </div>
);

const InputWrapper = ({ label, required, error, children }) => (
  <div className="space-y-2">
    <label className="block text-sm font-semibold text-gray-700">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
    {error && <p className="text-xs text-red-500 font-medium">{error}</p>}
  </div>
);

function OtherInformation() {
  const [formData, setFormData] = useState({
    residenceType: '',
    residenceAddress1: '',
    residenceAddress2: '',
    residenceLandmark: '',
    employerName: '',
    officialEmail: '',
    designation: '',
    ifsc: '',
    workMode: 'WFO',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [residencePincode, setResidencePincode] = useState('');
  const [personalEmail, setPersonalEmail] = useState('');
  const [checkingAuth, setCheckingAuth] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkLoginStatus = async () => {
      setCheckingAuth(true);
      const token = localStorage.getItem('token') || 
                    localStorage.getItem('authToken') || 
                    localStorage.getItem('authtoken');
      const profileId = localStorage.getItem('profile_id') || '';
      
      if (!token || !profileId) {
        navigate('/apply-now');
        return;
      }

      try {
        const response = await authAPI.getDashboardData(profileId);
        if (response.Status === 1) {
          if (response.Data?.profile_details?.residence_pincode) {
            setResidencePincode(response.Data.profile_details.residence_pincode);
          }
          if (response.Data?.profile_details?.personal_email) {
            setPersonalEmail(response.Data.profile_details.personal_email.toLowerCase());
          }
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setCheckingAuth(false);
      }
    };

    checkLoginStatus();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Apply input restrictions based on field type
    if (name === 'employerName' || name === 'designation') {
      // Only allow alphabets and spaces (no numbers, no special characters)
      processedValue = value.replace(/[^A-Za-z\s]/g, '');
    } else if (name === 'residenceAddress1' || name === 'residenceAddress2' || name === 'residenceLandmark') {
      // Allow alphabets, numbers, spaces, and common address characters
      processedValue = value.replace(/[^A-Za-z0-9\s,.-/]/g, '');
    } else if (name === 'officialEmail') {
      // Allow email characters: letters, numbers, @, ., _, -, +
      processedValue = value.replace(/[^A-Za-z0-9@._+-]/g, '');
    } else if (name === 'ifsc') {
      // Only allow alphanumeric, auto uppercase
      processedValue = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));
    setErrors((prev) => ({ ...prev, [name]: '', api: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!formData.residenceType) newErrors.residenceType = 'Residence type is required';
    if (!formData.residenceAddress1.trim())
      newErrors.residenceAddress1 = 'Address line 1 is required';
    if (!formData.employerName.trim()) newErrors.employerName = 'Employer name is required';
    if (formData.officialEmail.trim()) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.officialEmail.trim())) {
      newErrors.officialEmail = 'Please enter a valid email';
      } else if (personalEmail && formData.officialEmail.trim().toLowerCase() === personalEmail) {
        newErrors.officialEmail = 'Official email cannot be the same as personal email';
      }
    }
    if (!formData.designation.trim()) newErrors.designation = 'Designation is required';
    if (!formData.ifsc.trim()) {
      newErrors.ifsc = 'IFSC code is required';
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifsc.trim().toUpperCase())) {
      newErrors.ifsc = 'Please enter a valid IFSC code';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const selectedResidence = residenceOptions.find(
      (option) => option.value === formData.residenceType
    );

    if (!selectedResidence) {
      setErrors({ residenceType: 'Please select a valid residence type' });
      return;
    }

    const profileId = localStorage.getItem('profile_id') || '';
    if (!profileId) {
      setErrors({ api: 'Session expired. Please login again.' });
      navigate('/apply-now');
      return;
    }

    const getCurrentLocation = () =>
      new Promise((resolve) => {
        if (!navigator.geolocation) {
          resolve({ lat: '', lng: '' });
          return;
        }
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              lat: position.coords.latitude.toString(),
              lng: position.coords.longitude.toString(),
            });
          },
          () => resolve({ lat: '', lng: '' })
        );
      });

    setSubmitting(true);
    setErrors({});

    try {
      const location = await getCurrentLocation();

      const payload = {
        profile_id: profileId,
        residence_type_id: selectedResidence.id,
        residence_address_1: formData.residenceAddress1.trim(),
        residence_address_2: formData.residenceAddress2.trim(),
        residence_landmark: formData.residenceLandmark.trim(),
        residence_pincode: residencePincode || '',
        company_name: formData.employerName.trim(),
        office_email: formData.officialEmail.trim(),
        designation: formData.designation.trim(),
        department: '',
        account_ifsc: formData.ifsc.trim().toUpperCase(),
        emp_address_1: '',
        emp_address_2: '',
        emp_landmark: '',
        emp_pincode: residencePincode || '',
        work_mode: 'WFO', // Default work mode
        geo_lat: location.lat,
        geo_long: location.lng,
      };

      const response = await authAPI.submitOtherInformation(payload);

      if (response.Status === 1) {
        const nextEvent = response.Data?.next_step;
        if (nextEvent && eventToRouteMap[nextEvent]) {
          navigate(eventToRouteMap[nextEvent]);
        } else {
          navigate('/account-aggregator');
        }
      } else {
        setErrors({ api: response.Message || 'Failed to submit information. Please try again.' });
      }
    } catch (err) {
      setErrors({ api: err.message || 'Failed to submit information. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (checkingAuth) {
    return (
      <JourneyLayout journeyStep={4}>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-theme" />
        </div>
      </JourneyLayout>
    );
  }

  return (
    <JourneyLayout journeyStep={4}>
      <div className="text-center space-y-3 mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-theme/10 border border-theme/20 text-xs tracking-[0.3em] uppercase text-theme font-semibold">
          <Home className="h-4 w-4" />
          Snap Paisa Profile
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">Tell us about your lifestyle</h1>
        <p className="text-gray-600">
          Residence, employment, and salary details help us craft the best loan offer.
        </p>
      </div>

      <form className="space-y-6" onSubmit={handleSubmit}>
        <Section title="Residence Details">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputWrapper
              label="Residence Type"
              required
              error={errors.residenceType}
            >
              <select
                name="residenceType"
                value={formData.residenceType}
                onChange={handleChange}
                className={`w-full px-4 py-3 rounded-2xl bg-gray-50 border-2 text-gray-900 focus:outline-none transition-all ${
                  errors.residenceType
                    ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                    : 'border-gray-300 focus:border-theme focus:ring-2 focus:ring-theme/20'
                }`}
              >
                <option value="" className="text-gray-900">Select residence type</option>
                {residenceOptions.map((option) => (
                  <option key={option.value} value={option.value} className="text-gray-900">
                    {option.label}
                  </option>
                ))}
              </select>
            </InputWrapper>

            <InputWrapper
              label="Address Line 1"
              required
              error={errors.residenceAddress1}
            >
              <input
                type="text"
                name="residenceAddress1"
                value={formData.residenceAddress1}
                onChange={handleChange}
                placeholder="Flat, Street"
                autoComplete="off"
                maxLength={200}
                className={`w-full px-4 py-3 rounded-2xl bg-gray-50 border-2 text-gray-900 placeholder:text-gray-400 focus:outline-none transition-all ${
                  errors.residenceAddress1
                    ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                    : 'border-gray-300 focus:border-theme focus:ring-2 focus:ring-theme/20'
                }`}
              />
            </InputWrapper>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputWrapper label="Address Line 2">
              <input
                type="text"
                name="residenceAddress2"
                value={formData.residenceAddress2}
                onChange={handleChange}
                placeholder="Area / Locality"
                autoComplete="off"
                maxLength={200}
                className="w-full px-4 py-3 rounded-2xl bg-gray-50 border-2 border-gray-300 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-theme focus:ring-2 focus:ring-theme/20 transition-all"
              />
            </InputWrapper>

            <InputWrapper label="Landmark">
              <input
                type="text"
                name="residenceLandmark"
                value={formData.residenceLandmark}
                onChange={handleChange}
                placeholder="Nearby landmark"
                autoComplete="off"
                maxLength={100}
                className="w-full px-4 py-3 rounded-2xl bg-gray-50 border-2 border-gray-300 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-theme focus:ring-2 focus:ring-theme/20 transition-all"
              />
            </InputWrapper>
          </div>
        </Section>

        <Section title="Employment Details">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputWrapper label="Employer Name" required error={errors.employerName}>
              <input
                type="text"
                name="employerName"
                value={formData.employerName}
                onChange={handleChange}
                placeholder="Company name"
                autoComplete="off"
                maxLength={100}
                className={`w-full px-4 py-3 rounded-2xl bg-gray-50 border-2 text-gray-900 placeholder:text-gray-400 focus:outline-none transition-all ${
                  errors.employerName
                    ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                    : 'border-gray-300 focus:border-theme focus:ring-2 focus:ring-theme/20'
                }`}
              />
            </InputWrapper>

            <InputWrapper label="Official Email" error={errors.officialEmail}>
              <input
                type="email"
                name="officialEmail"
                value={formData.officialEmail}
                onChange={handleChange}
                placeholder="official@email.com"
                autoComplete="new-password"
                maxLength={100}
                className={`w-full px-4 py-3 rounded-2xl bg-gray-50 border-2 text-gray-900 placeholder:text-gray-400 focus:outline-none transition-all ${
                  errors.officialEmail
                    ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                    : 'border-gray-300 focus:border-theme focus:ring-2 focus:ring-theme/20'
                }`}
              />
            </InputWrapper>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputWrapper label="Designation" required error={errors.designation}>
              <input
                type="text"
                name="designation"
                value={formData.designation}
                onChange={handleChange}
                placeholder="Your role"
                autoComplete="off"
                maxLength={50}
                className={`w-full px-4 py-3 rounded-2xl bg-gray-50 border-2 text-gray-900 placeholder:text-gray-400 focus:outline-none transition-all ${
                  errors.designation
                    ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                    : 'border-gray-300 focus:border-theme focus:ring-2 focus:ring-theme/20'
                }`}
              />
            </InputWrapper>

            <InputWrapper label="Salary IFSC" required error={errors.ifsc}>
              <input
                type="text"
                name="ifsc"
                value={formData.ifsc}
                onChange={handleChange}
                placeholder="SBIN0000001"
                autoComplete="off"
                maxLength={11}
                className={`w-full px-4 py-3 rounded-2xl bg-gray-50 border-2 text-gray-900 placeholder:text-gray-400 focus:outline-none transition-all uppercase ${
                  errors.ifsc
                    ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                    : 'border-gray-300 focus:border-theme focus:ring-2 focus:ring-theme/20'
                }`}
              />
            </InputWrapper>
          </div>
        </Section>

        {errors.api && (
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 text-sm text-red-700">
            {errors.api}
          </div>
        )}

        <div className="pt-2">
          <button
            type="submit"
            disabled={submitting}
            className="w-full sm:w-auto bg-gradient-to-r from-theme to-theme2 text-white px-10 py-4 rounded-2xl font-semibold text-lg flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg hover:shadow-xl transition-all"
          >
            {submitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Submitting...</span>
              </>
            ) : (
              <>
                <span>Continue</span>
                <ArrowRight className="h-5 w-5" />
              </>
            )}
          </button>
        </div>
      </form>
    </JourneyLayout>
  );
}

export default OtherInformation;

